import type { UUID } from '@aline/common';
import { useActivityReplyHandler } from 'src/components';
import type {
  ActivityItemFragment,
  OnSubmitCommentFn,
  OnSubmitCommentReturn,
} from 'src/components';
import type { ActivityNavigation } from 'src/components/activity/use-activity-navigation';
import { useStartAiThreadFromActivity } from 'src/components/activity/use-start-ai-thread';
import { Apollo } from 'src/gql/generated';
import { useCreateTaskActivityComment, useTaskActivity } from 'src/hooks';

interface TaskActivityController {
  activity: ActivityItemFragment[];
  addActivityItem: (row: ActivityItemFragment) => void;
  fetchMoreActivity: () => Promise<unknown>;
  fetchPreviousActivity: () => Promise<unknown>;
  hasMore: boolean;
  hasPrevious: boolean;
  loadedAnchor: UUID | null;
  loading: boolean;
  loadingMore: boolean;
  loadingPrevious: boolean;
  navigation: ActivityNavigation;
  onSubmitAI: (markdown: string) => Promise<boolean>;
  onSubmitComment: OnSubmitCommentFn;
  onSubmitReply: (parentCommentId: UUID, markdown: string) => Promise<OnSubmitCommentReturn>;
  workflowId: UUID;
}

interface TaskActivityControllerOptions {
  /** Deep-link state for this task's feed (see `useActivityNavigation`). */
  navigation: ActivityNavigation;
  /** Read one lane of the feed; null reads all of it. */
  audience?: Apollo.ActivityAudience | null;
  skip?: boolean;
}

/**
 * Owns the data + write handlers for a task's Activity feed. The panel renders the feed and the
 * composer in separate regions (composer docks above the "Mark Complete" footer), so this is a
 * controller hook rather than a single component.
 */
export function useTaskActivityController(
  taskId: UUID,
  workflowId: UUID,
  options: TaskActivityControllerOptions
): TaskActivityController {
  const { navigation } = options;
  const {
    activity,
    addActivityItem,
    fetchMoreActivity,
    fetchPreviousActivity,
    hasMore,
    hasPrevious,
    loadedAnchor,
    loading,
    loadingMore,
    loadingPrevious,
  } = useTaskActivity(taskId, {
    anchor: navigation.request?.activityItemId,
    audience: options.audience ?? null,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: options.skip,
    // Task views read the feed below the task's fields: oldest first, newer pages load below.
    startAtOldest: true,
  });
  const { createTaskActivityComment: onSubmitComment } = useCreateTaskActivityComment(taskId);

  const onSubmitReply = useActivityReplyHandler({
    commentableId: taskId,
    commentableType: Apollo.CommentableType.Task,
  });

  const onSubmitAI = useStartAiThreadFromActivity({ taskId });

  return {
    activity,
    addActivityItem,
    fetchMoreActivity,
    fetchPreviousActivity,
    hasMore,
    hasPrevious,
    loadedAnchor,
    loading,
    loadingMore,
    loadingPrevious,
    navigation,
    onSubmitAI,
    onSubmitComment,
    onSubmitReply,
    workflowId,
  };
}
